/**
 * Applies a callback function to each entry in an object, reducing the object to a single accumulated value.
 *
 * ...it's an object reducer that supports additional parameters for recursive calls
 *
 * TODO: Consider simplifying the approach to recursive reduceObject calls
 *
 * @param {Object} obj - The object to iterate over.
 * @param {function} callback - The function to apply to each entry.
 * @param {Object} [base={}] - The initial value for the accumulated result.
 * @param {...*} additionalArgs - Additional arguments to pass to the callback function.
 * @returns {Object} - The accumulated result after applying the callback function to each entry.
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * const result = reduceObject(obj, (acc, [key, value], flatIncrease) => ({...acc, [key]: value * 2 + flatIncrease }), {}, 2);
 * console.log(result); // { a: 4, b: 6, c: 8 }
 */
export const reduceObject = (obj, callback, base = {}, ...additionalArgs) =>
  Object.entries(obj).reduce((acc, entry) => {
    return callback(acc, entry, ...additionalArgs)
  }, base)

/**
 * A generator function that iterates over the entries of an object, applying a callback function to each entry.
 * It supports nested objects and arrays, and can modify the keys and values of the entries.
 * Due to its use of flatMap, items can be added or removed in the callback via array syntax returns
 *
 * @generator
 * @param {Object} obj - The object to iterate over.
 * @param {function} callback - The function to apply to each entry.
 * @param {'entry'|'key'|'value'} [callbackParam='entry'] - The parameter to pass to the callback function.
 * @param {Object} [original=obj] - The original object, passed to the callback function. Value is set automatically..
 * @param {string} [key=''] - The current key, used for recursive calls. Value is set automatically.
 * @yields {Array} - The modified key-value pair.
 *
 * @throws {Error} - If the input object is not an object.
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * const result = [...mapEntries(obj, ([key, value]) => [key, value * 2])];
 * console.log(result); // Output: [['a', 2], ['b', 4], ['c', 6]]
 */
export function* mapEntries(obj, callback, callbackParam = 'entry', original = obj, key = '', parentKeys = []) {
  const keys = [...parentKeys, key !== '' ? key : null].filter(Boolean)

  const runCallback = ([entryKey, entryValue]) => {
    switch (callbackParam) {
      case 'key':
        entryKey = callback(entryKey, keys, original)
        break
      case 'value':
        entryValue = callback(entryValue, keys, original)
        break
      default:
        return callback([entryKey, entryValue], keys, original)
    }

    return [entryKey, entryValue]
  }

  if (typeof obj !== 'object') {
    if (obj === original) {
      throw new Error('Cannot map entries of non-object')
    }

    yield runCallback([key, obj])
    return
  }

  for (const entry of Object.entries(obj)) {
    const [entryKey, entryValue] = entry
    if (Array.isArray(entryValue)) {
      entry[1] = entryValue.flatMap(item =>
        typeof item === 'object'
          ? Object.fromEntries([...mapEntries(item, callback, callbackParam, original, entryKey, keys)])
          : item
      )
    } else if (typeof entryValue === 'object') {
      entry[1] = Object.fromEntries([...mapEntries(entryValue, callback, callbackParam, original, entryKey, keys)])
    }

    const result = runCallback(entry)

    if (Array.isArray(result) && Array.isArray(result[0])) {
      yield* result
    } else {
      yield result
    }
  }
}

/**
 * Iterates over the entries of an object, applying a callback function to each entry.
 * Returns a new object from the modified entries.
 *
 * @param {Object} obj - The object to iterate over.
 * @param {function} callback - The function to apply to each entry.
 * @param {'entry'|'key'|'value'} [callbackParam='entry'] - The parameter to pass to the callback function.
 * @returns {Object} - The modified object, with keys and values transformed by the callback function.
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 }
 * const result = mapObjectEntries(obj, ([key, value]) => [key, value * 2])
 * console.log(result) // Output: { a: 2, b: 4, c: 6 }
 */
export const mapObjectEntries = (obj, callback, callbackParam = 'entry') =>
  Object.fromEntries([...mapEntries(obj, callback, callbackParam)])

/**
 * Iterates over the entries of an object, applying a callback function to each key.
 * Returns a new object from the modified keys.
 *
 * @param {Object} obj - The object to iterate over.
 * @param {function} callback - The function to apply to each key.
 * @returns {Object} - The modified object, with keys transformed by the callback function.
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 }
 * const result = mapObjectKeys(obj, key => key.toUpperCase())
 * console.log(result) // Output: { A: 1, B: 2, C: 3 }
 */
export const mapObjectKeys = (obj, callback) => Object.fromEntries([...mapEntries(obj, callback, 'key')])

/**
 * Iterates over the entries of an object, applying a callback function to each value.
 * Returns a new object from the modified values.
 *
 * @param {Object} obj - The object to iterate over.
 * @param {function} callback - The function to apply to each value.
 * @returns {Object} - The modified object, with values transformed by the callback function.
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 }
 * const result = mapObjectValues(obj, value => value * 2)
 * console.log(result) // Output: { a: 2, b: 4, c: 6 }
 */
export const mapObjectValues = (obj, callback) => Object.fromEntries([...mapEntries(obj, callback, 'value')])

export const maybeTransform = (value, transform) => (transform ? transform(value) : value)

// Convert an array of objects into a key-value dict
export const arrayToDict = (objects, { key = 'key', valueKey, keyTransform, valueTransform } = {}) =>
  Object.fromEntries(objects.map(item => [maybeTransform(item[key], keyTransform), maybeTransform(valueKey ? item[valueKey] : item, valueTransform)]))

/**
 * Maps each element of an array to an array and flattens the result by one level.
 * Similar to Array.prototype.flatMap but with support for additional arguments and improved performance (initial benchmarks show ~1.75x ops/sec).
 *
 * @param {Array} array - The array to map and flatten
 * @param {function} callback - Function that produces an element of the new array
 * @param {...*} args - Additional arguments to pass to the callback
 * @returns {Array} A new array with each element being the result of the callback function and flattened by one level
 *
 * @example
 * const numbers = [1, 2, 3];
 * const result = simpleFlatMap(numbers, n => [n, n * 2]);
 * console.log(result); // [1, 2, 2, 4, 3, 6]
 */
export const simpleFlatMap = (array, callback, ...args) =>
  array.reduce((acc, item) => {
    acc.push(...callback(item, ...args));
    return acc;
  }, [])

/**
 * Get the object type
 * @param  {*}       obj The object
 * @return {String}      The object type
 *
 * @example
 * getType(new Date()) // 'date'
 * getType(/regex/) // 'regexp'
 */
export const getType = obj => Object.prototype.toString.call(obj).slice(8, -1).toLowerCase()

/**
 * Merges two objects recursively, creating a new object with the combined properties.
 * Arrays and nested objects are deeply merged, while primitive values are overwritten.
 * Intended for use in the deepMerge function.
 * 
 * @param {Object} obj1 - The base object to merge into
 * @param {Object} obj2 - The object whose properties will be merged into obj1
 * @returns {Object} A new object containing the merged properties
 * 
 * @example
 * const obj1 = { a: 1, b: { x: 1, y: 2 }, c: [1, 2] }
 * const obj2 = { b: { z: 3 }, c: [3, 4], d: 4 }
 * const result = _mergeObjects(obj1, obj2) // { a: 1, b: { x: 1, y: 2, z: 3 }, c: [1, 2, 3, 4], d: 4 }
 */
export const _mergeObjects = (obj1, obj2) => {
  const clone = structuredClone(obj1)
  for (const [key, value] of Object.entries(obj2)) {
    const type = getType(value)
    if (clone[key] !== undefined && getType(clone[key]) === type && ['array', 'object'].includes(type)) {
      clone[key] = deepMerge(clone[key], value)
    } else {
      clone[key] = structuredClone(value)
    }
  }

  return clone
}

/**
 * Deeply merges multiple objects or arrays into a single new object/array.
 * For objects, properties are recursively merged. For arrays, elements are concatenated.
 * If types don't match between objects, the last object's type takes precedence.
 * 
 * @param {...Object|Array} objs - The objects or arrays to merge
 * @returns {Object|Array} A new object/array containing the merged values
 * 
 * @example
 * const obj1 = { a: 1, b: { x: 1 } }
 * const obj2 = { b: { y: 2 }, c: 3 }
 * const obj3 = { d: 4 }
 * const result = deepMerge(obj1, obj2, obj3)
 * // result = { a: 1, b: { x: 1, y: 2 }, c: 3, d: 4 }
 * 
 * @example
 * const arr1 = [1, 2]
 * const arr2 = [3, 4]
 * const result = deepMerge(arr1, arr2)
 * // result = [1, 2, 3, 4]
 */
export const deepMerge = (...objs) => {
  let clone = structuredClone(objs.shift())
  for (const obj of objs) {
    const type = getType(obj)
    if (getType(clone) !== type) {
      clone = structuredClone(obj)
      continue
    }

    if (type === 'array') {
      clone = [...clone, ...structuredClone(obj)]
    } else if (type === 'object') {
      clone = _mergeObjects(clone, obj)
    } else {
      clone = obj
    }
  }

  return clone
}

/**
 * Converts a flat object with dot-notation keys into a nested object structure.
 * Each key is split on the provided separator to create the nested hierarchy.
 *
 * @param {Object} obj - The flat object to convert
 * @returns {Object} A nested object with the same values but hierarchical structure
 *
 * @example
 * const flat = { "a.b.c": 1, "a.b.d": 2 }
 * const nested = dimensionalize(flat) // Result: { a: { b: { c: 1, d: 2 } } }
 */
export const dimensionalize = (obj, separator = '.') => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const keys = key.split(separator);
    let current = acc;

    keys.forEach((k, i) => {
      if (i === keys.length - 1) {
        current[k] = value;
      } else {
        current[k] = current[k] || {};
        current = current[k];
      }
    });

    return acc;
  }, {});
};

export const getDependency = async (modulePath, { type = 'js' } = {}) => {
  let module
  switch (type) {
    case 'css':
      module = await import(`../../../node_modules/${modulePath}.css`)
      break
    case 'mjs':
      module = await import(`../../../node_modules/${modulePath}.mjs`)
      break
    default:
      module = await import(`../../../node_modules/${modulePath}.js`)
      break
  }
  return module.default
}